/**
 * RideTracking Component - Real-time map tracking for active rides
 */
import { useState, useEffect, useRef } from 'react';
import { useRideTracking } from '../hooks/useRideTracking';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Phone, MessageCircle, Navigation, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const driverIcon = new L.DivIcon({
  className: 'custom-driver-marker',
  html: `<div style="background: #22c55e; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M5 11l1.5-4.5h11L19 11m-1.5 5a1.5 1.5 0 01-1.5-1.5 1.5 1.5 0 011.5-1.5 1.5 1.5 0 011.5 1.5 1.5 1.5 0 01-1.5 1.5m-11 0A1.5 1.5 0 015 14.5 1.5 1.5 0 016.5 13 1.5 1.5 0 018 14.5 1.5 1.5 0 016.5 16M18.92 6c-.2-.58-.76-1-1.42-1H6.5c-.66 0-1.22.42-1.42 1L3 12v8a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-8l-2.08-6z"/></svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

const pickupIcon = new L.DivIcon({
  className: 'custom-pickup-marker',
  html: `<div style="background: #22c55e; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3); font-weight: bold; color: white; font-size: 16px;">P</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const dropoffIcon = new L.DivIcon({
  className: 'custom-dropoff-marker',
  html: `<div style="background: #ef4444; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3); font-weight: bold; color: white; font-size: 16px;">D</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

// Component to center map on driver location
const MapCenterController = ({ center, shouldCenter }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && shouldCenter) {
      map.setView(center, map.getZoom());
    }
  }, [center, shouldCenter, map]);
  
  return null;
};

const RideTracking = ({ 
  rideId, 
  userId, 
  pickup, 
  dropoff, 
  onCallDriver, 
  onChatDriver,
  showDriverInfo = true,
  height = "400px" 
}) => {
  const {
    driverLocation,
    isRealtime,
    isDriverConnected,
    rideStatus,
    driverInfo,
    isConnected,
    refreshLocation
  } = useRideTracking(rideId, userId);

  const [centerOnDriver, setCenterOnDriver] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const mapRef = useRef(null);

  // Update last update time
  useEffect(() => {
    if (driverLocation) {
      setLastUpdate(new Date());
    }
  }, [driverLocation]);

  // Calculate map center
  const mapCenter = driverLocation 
    ? [driverLocation.lat, driverLocation.lng]
    : pickup 
      ? [pickup.lat, pickup.lng]
      : [12.9716, 77.5946]; // Default: Bangalore

  // Calculate route polyline
  const routePoints = [];
  if (pickup) routePoints.push([pickup.lat, pickup.lng]);
  if (driverLocation) routePoints.push([driverLocation.lat, driverLocation.lng]);
  if (dropoff) routePoints.push([dropoff.lat, dropoff.lng]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      accepted: 'bg-blue-500',
      arrived: 'bg-green-500',
      in_progress: 'bg-purple-500',
      completed: 'bg-gray-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Finding Driver...',
      accepted: 'Driver on the way',
      arrived: 'Driver Arrived',
      in_progress: 'Ride in Progress',
      completed: 'Ride Completed',
      cancelled: 'Ride Cancelled'
    };
    return texts[status] || status;
  };

  return (
    <div className="ride-tracking-container" data-testid="ride-tracking">
      {/* Status Bar */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(rideStatus)}>
              {getStatusText(rideStatus)}
            </Badge>
            {isRealtime ? (
              <Badge variant="outline" className="text-green-500 border-green-500">
                <Wifi className="w-3 h-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                <WifiOff className="w-3 h-3 mr-1" />
                Polling
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshLocation}
              data-testid="refresh-location-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant={centerOnDriver ? "default" : "outline"}
              size="sm"
              onClick={() => setCenterOnDriver(!centerOnDriver)}
              data-testid="center-on-driver-btn"
            >
              <Navigation className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {lastUpdate && (
          <p className="text-xs text-muted-foreground mt-2">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Driver Info Card */}
      {showDriverInfo && driverInfo && (rideStatus === 'accepted' || rideStatus === 'arrived' || rideStatus === 'in_progress') && (
        <div className="bg-card border border-border rounded-lg p-4 mb-4" data-testid="driver-info-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                {driverInfo.name?.charAt(0) || 'D'}
              </div>
              <div>
                <h4 className="font-medium">{driverInfo.name}</h4>
                <p className="text-sm text-muted-foreground">{driverInfo.vehicle}</p>
                {driverInfo.rating && (
                  <div className="flex items-center gap-1 text-sm">
                    <span className="text-yellow-500">★</span>
                    <span>{driverInfo.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {onChatDriver && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onChatDriver}
                  data-testid="chat-driver-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                </Button>
              )}
              {onCallDriver && driverInfo.phone && (
                <Button
                  variant="default"
                  size="icon"
                  onClick={() => onCallDriver(driverInfo.phone)}
                  data-testid="call-driver-btn"
                >
                  <Phone className="w-5 h-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div 
        className="rounded-lg overflow-hidden border border-border"
        style={{ height }}
        data-testid="tracking-map"
      >
        <MapContainer
          center={mapCenter}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapCenterController 
            center={driverLocation ? [driverLocation.lat, driverLocation.lng] : null}
            shouldCenter={centerOnDriver && driverLocation}
          />

          {/* Driver Marker */}
          {driverLocation && (
            <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{driverInfo?.name || 'Driver'}</strong>
                  <br />
                  {isRealtime ? (
                    <span className="text-green-600 text-sm">● Live Location</span>
                  ) : (
                    <span className="text-yellow-600 text-sm">◐ Last Known</span>
                  )}
                </div>
              </Popup>
            </Marker>
          )}

          {/* Pickup Marker */}
          {pickup && (
            <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
              <Popup>
                <strong>Pickup</strong>
                <br />
                {pickup.address || 'Pickup Location'}
              </Popup>
            </Marker>
          )}

          {/* Dropoff Marker */}
          {dropoff && (
            <Marker position={[dropoff.lat, dropoff.lng]} icon={dropoffIcon}>
              <Popup>
                <strong>Destination</strong>
                <br />
                {dropoff.address || 'Drop-off Location'}
              </Popup>
            </Marker>
          )}

          {/* Route Line */}
          {routePoints.length > 1 && (
            <Polyline
              positions={routePoints}
              color="#6366f1"
              weight={4}
              opacity={0.7}
              dashArray="10, 10"
            />
          )}
        </MapContainer>
      </div>

      {/* Connection Status */}
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          WebSocket: {isConnected ? (
            <span className="text-green-500">Connected</span>
          ) : (
            <span className="text-red-500">Disconnected</span>
          )}
        </span>
        {isDriverConnected && (
          <span className="text-green-500">Driver Online</span>
        )}
      </div>
    </div>
  );
};

export default RideTracking;
